import React, { useMemo, useRef, useState } from 'react'
import DataGrid, { Column, Editing, Paging, Sorting } from 'devextreme-react/data-grid'
import 'devextreme/ui/select_box'
import 'devextreme/ui/date_box'
import 'devextreme/ui/number_box'
import type { ActionInput, PopupAction } from './types'

export const isInputVisible = (input: ActionInput, formData: Record<string, any>) =>
	!input.visibleWhen || input.visibleWhen.in.includes(formData[input.visibleWhen.field])

const EDITORS = {
	text: 'dxTextBox',
	textarea: 'dxTextArea',
	select: 'dxSelectBox',
	date: 'dxDateBox',
	number: 'dxNumberBox',
} as const

const editorOptionsFor = (input: ActionInput) => {
	switch (input.type) {
		case 'select': {
			const items = (input.options ?? []).map(o => (typeof o === 'string' ? { value: o, text: o } : o))
			return {
				...(input.search
					? {
							dataSource: {
								store: { type: 'array', data: items, key: 'value' },
								paginate: true,
								pageSize: 50,
							},
							searchEnabled: true,
							searchExpr: 'text',
					  }
					: { items }),
				valueExpr: 'value',
				displayExpr: 'text',
				showClearButton: true,
				wrapItemText: true,
				dropDownOptions: { width: 'auto', minWidth: 320, maxWidth: 640 },
			}
		}
		case 'date':
			return { type: 'date', displayFormat: 'dd/MM/yyyy', dateSerializationFormat: 'yyyy-MM-dd' }
		default:
			return { placeholder: input.placeholder }
	}
}

// One row per input: its name, the read-only reference value and an always-open DevExtreme editor.
// formData is written as the user edits; a row with visibleWhen follows the value of another input.
export function InputTable(props: {
	inputs: ActionInput[]
	columns: NonNullable<PopupAction['columns']>
	formData: Record<string, any>
	readOnly?: boolean
}): JSX.Element {
	const { inputs, columns, formData, readOnly } = props
	const [, rerender] = useState(0)
	const initial = useRef({ ...formData }).current
	const byName = useMemo(() => Object.fromEntries(inputs.map(i => [i.name, i])), [inputs])
	const rows = inputs
		.filter(input => isInputVisible(input, formData))
		.map(input => ({
			name: input.name,
			label: input.label ?? input.name,
			reference: input.reference ?? '',
			value: formData[input.name] ?? null,
			reason: input.reason ? formData[input.reason.name] ?? null : null,
		}))

	return (
		<DataGrid
			dataSource={rows}
			keyExpr="name"
			showBorders={false}
			showRowLines
			showColumnLines={false}
			wordWrapEnabled
			hoverStateEnabled={false}
			onEditorPreparing={e => {
				if (e.parentType !== 'dataRow') return
				const input = byName[e.row?.data.name as string]
				if (!input) return
				if (e.dataField === 'reason') {
					if (!input.reason || readOnly) {
						e.cancel = true
						return
					}
					const reasonName = input.reason.name
					e.editorName = EDITORS.text
					e.editorOptions = {
						...e.editorOptions,
						placeholder: input.reason.placeholder,
						value: formData[reasonName] ?? null,
						stylingMode: 'outlined',
						valueChangeEvent: 'input',
						onValueChanged: (change: any) => {
							formData[reasonName] = change.value
						},
					}
					return
				}
				if (e.dataField !== 'value') return
				e.editorName = EDITORS[(input.type ?? 'text') as keyof typeof EDITORS] ?? EDITORS.text
				e.editorOptions = {
					...e.editorOptions,
					...editorOptionsFor(input),
					value: formData[input.name] ?? null,
					stylingMode: readOnly ? 'underlined' : 'outlined',
					readOnly,
					onValueChanged: (change: any) => {
						formData[input.name] = change.value
						change.element.classList.toggle(
							'clickable-popup-edited',
							(change.value ?? null) !== (initial[input.name] ?? null)
						)
						if (inputs.some(i => i.visibleWhen?.field === input.name)) rerender(n => n + 1)
					},
				}
			}}
		>
			<Editing mode="cell" allowUpdating={!readOnly} />
			<Sorting mode="none" />
			<Paging enabled={false} />
			<Column
				dataField="label"
				caption={columns.field}
				allowEditing={false}
				width={columns.reason ? '22%' : '30%'}
			/>
			<Column
				dataField="reference"
				caption={columns.reference}
				allowEditing={false}
				width={columns.reason ? '22%' : '30%'}
				visible={inputs.some(i => i.reference)}
			/>
			<Column dataField="value" caption={columns.value} showEditorAlways />
			<Column
				dataField="reason"
				caption={columns.reason}
				showEditorAlways
				visible={!!columns.reason && !readOnly}
			/>
		</DataGrid>
	)
}
